// PlanPicker — inline `<select>` for a subscription plan, sourced from
// the admin catalog. Shared between the Users list (PX-119) and the
// UserDetail page (PX-120) so plan visuals + inactive-handling stay
// consistent in one place.

import type { AdminPlanResponse } from '@/types/api'

export function PlanPicker({
  value,
  fallbackSlug,
  plans,
  onChange,
  disabled,
}: {
  value: number
  fallbackSlug: string
  plans: AdminPlanResponse[]
  onChange: (next: number) => void
  disabled?: boolean
}) {
  const current = plans.find((p) => p.id === value) ?? null
  const palette = planVisualPalette(current)
  // Active plans plus the current one even if it's been deactivated —
  // never silently drop the user's existing assignment from the list.
  const visible = plans.filter((p) => p.is_active || p.id === value)
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={disabled}
      className={`rounded-md border px-2 py-1 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-brand-100 ${palette} disabled:cursor-not-allowed disabled:opacity-60`}
    >
      {/* If the current plan_id isn't in the catalog at all (deleted
          row server-side, or catalog still loading) keep it as an
          option so the dropdown reflects the row's actual value. */}
      {!plans.some((p) => p.id === value) && (
        <option value={value}>{fallbackSlug} (unknown)</option>
      )}
      {visible.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}{!p.is_active ? ' · inactive' : ''}
        </option>
      ))}
    </select>
  )
}

// PlanMultiPicker — chip toggles for a list of plan ids (PX-149). Backs
// `models.allowed_plan_ids`: an EMPTY selection means "every plan" per
// the rank-gate convention (an empty allowed list never restricts). We
// surface that explicitly so the operator isn't surprised that clearing
// all chips = ungated rather than "nobody".
export function PlanMultiPicker({
  value,
  plans,
  onChange,
  disabled,
}: {
  value: number[]
  plans: AdminPlanResponse[]
  onChange: (next: number[]) => void
  disabled?: boolean
}) {
  const selected = new Set(value)
  // Active plans plus any already-selected id even if it's been
  // deactivated — never silently drop an existing restriction.
  const visible = plans.filter((p) => p.is_active || selected.has(p.id))
  const toggle = (id: number) => {
    if (disabled) return
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange([...next].sort((a, b) => a - b))
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map((p) => {
        const on = selected.has(p.id)
        return (
          <button
            key={p.id}
            type="button"
            disabled={disabled}
            aria-pressed={on}
            onClick={() => toggle(p.id)}
            className={`rounded-full border px-2 py-0.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-60 ${
              on
                ? 'border-brand-300 bg-brand-50 text-brand-700'
                : 'border-ink-200 bg-white text-ink-500 hover:border-ink-300'
            }`}
          >
            {p.name}{!p.is_active ? ' · inactive' : ''}
          </button>
        )
      })}
      {value.length === 0 && (
        <span className="text-xs italic text-ink-400">All plans (ungated)</span>
      )}
    </div>
  )
}

export function planVisualPalette(plan: AdminPlanResponse | null): string {
  if (!plan) return 'border-ink-200 bg-white text-ink-700'
  if (plan.unlimited_generations) return 'border-amber-300 bg-amber-50 text-amber-800'
  if (plan.slug === 'free') return 'border-ink-200 bg-white text-ink-700'
  return 'border-brand-300 bg-brand-50 text-brand-700'
}

export function PlanTag({
  plan,
  fallbackSlug,
}: {
  plan: AdminPlanResponse | null
  fallbackSlug: string
}) {
  const palette = planVisualPalette(plan)
  const label = plan?.name ?? fallbackSlug
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 font-mono text-[11px] font-medium ${palette}`}>
      {label}
    </span>
  )
}
